import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, XCircle, Filter } from "lucide-react";

const STATUS_COLORS = {
  paid:      "bg-emerald-50 text-emerald-700",
  pending:   "bg-amber-50 text-amber-700",
  cancelled: "bg-red-50 text-red-600",
  failed:    "bg-slate-100 text-slate-500",
};

const PLAN_LABELS = {
  monthly:   "Silver · 1mo",
  quarterly: "Gold · 3mo",
  yearly:    "Diamond · 1yr",
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AdminSubscriptions() {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [planFilter, setPlanFilter]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [cancelling, setCancelling]     = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (planFilter)   params.set("plan",   planFilter);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/admin/subscriptions?${params}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [planFilter, statusFilter]); // eslint-disable-line

  const handleCancel = async (sub) => {
    if (!window.confirm(`Cancel subscription for ${sub.user_name}? This will revoke their premium access.`)) return;
    setCancelling(sub.id);
    try {
      await api.put(`/admin/subscriptions/${sub.id}/cancel`);
      toast.success("Subscription cancelled and premium revoked.");
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Subscriptions"
        subtitle={`${total} total payment orders`}
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-white rounded-xl border border-slate-100 px-4 py-3 shadow-sm">
        <Filter className="w-4 h-4 text-slate-400" />
        <select
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="">All Plans</option>
          <option value="monthly">Silver (Monthly)</option>
          <option value="quarterly">Gold (Quarterly)</option>
          <option value="yearly">Diamond (Yearly)</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="">All Statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
          <option value="failed">Failed</option>
        </select>
        {(planFilter || statusFilter) && (
          <button
            onClick={() => { setPlanFilter(""); setStatusFilter(""); }}
            className="text-xs text-slate-400 hover:text-slate-600 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading subscriptions…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <CreditCard className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No subscriptions found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-w-full">
            <table className="w-full min-w-[800px] text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400">User</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Plan</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Amount</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Status</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Order Date</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Paid At</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Payment ID</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {items.map((sub) => (
                  <tr key={sub.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-medium text-slate-800">{sub.user_name}</div>
                      <div className="text-xs text-slate-400">{sub.user_email}</div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {PLAN_LABELS[sub.plan] || sub.plan}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-semibold text-slate-700">
                      {sub.amount > 0 ? `₹${sub.amount.toLocaleString()}` : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[sub.status] || "bg-slate-100 text-slate-500"}`}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-500">{fmt(sub.created_at)}</td>
                    <td className="px-5 py-4 text-slate-500">{fmt(sub.paid_at)}</td>
                    <td className="px-5 py-4">
                      <span className="font-mono text-xs text-slate-400 break-all">
                        {sub.payment_id || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      {sub.status === "paid" && (
                        <button
                          onClick={() => handleCancel(sub)}
                          disabled={cancelling === sub.id}
                          className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          {cancelling === sub.id ? "Cancelling…" : "Cancel"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
